import React, { useEffect, useState } from 'react';
import { Box, TextField, Typography } from '@mui/material';
import { IndicatorConfigInput } from '@server/data-feed/schemas/input.schema';
import type { TemplateConfigInput } from '@server/trading-templates/schemas/input.schema';
import { AppButton } from '@client/foundation/design-system/components';
import { useWindow } from '@client/window/window.hook';
import { FormProvider, useForm } from 'react-hook-form';
import { TradingFormState } from '@client/widgets/trading-form/trading-form.types';
import { getIndicatorsPath } from '@client/widgets/trading-form/indicators/indicator-config-form.utils';
import { IndicatorsArrayForm } from '@client/widgets/trading-form/indicators/indicator-config-form';
import { useTemplateSave } from '@client/features/trading-templates/template-save.hook';
import {
  formIndicatorsToTemplateValue
} from '@client/features/trading-templates/indicators-template-resolver';
import { templateEditorSurfaceSx } from './template-editor.styles';

export type IndicatorTemplateEditorWindowProps = {
  templateId?: string;
  initialName?: string;
  initialIndicators?: IndicatorConfigInput[];
  onSaved?: (savedTemplate: {
    templateId: string;
    name: string;
    config: TemplateConfigInput;
  }) => void;
};

type IndicatorTemplateEditorProps = IndicatorTemplateEditorWindowProps & {
  onClose: () => void;
};
type IndicatorTemplateConfig = Extract<
  TemplateConfigInput,
  { type: 'indicators' }
>;

const emptyIndicators: IndicatorConfigInput[] = [];
const indicatorsPath = getIndicatorsPath(0);

const buildEditorFormState = (
  indicators: IndicatorConfigInput[]
): TradingFormState => ({
  timeframes: [
    {
      timeframe: '1m',
      indicators
    }
  ]
});

export const useIndicatorTemplateEditor = ({
  templateId,
  initialName = '',
  initialIndicators = emptyIndicators,
  onSaved,
  onClose
}: IndicatorTemplateEditorProps) => {
  const formApi = useForm<TradingFormState>({
    defaultValues: buildEditorFormState(initialIndicators)
  });
  const { control, reset, getValues } = formApi;
  const [name, setName] = useState(initialName);
  const [error, setError] = useState('');
  const isEditMode = Boolean(templateId);

  useEffect(() => {
    setName(initialName);
    reset(buildEditorFormState(initialIndicators));
    setError('');
  }, [initialIndicators, initialName, reset, templateId]);

  const { saveTemplate, isPending } = useTemplateSave();
  const loading = isPending;

  const handleSave = async () => {
    const trimmedName = name.trim();

    if (!trimmedName) {
      setError('Template name is required.');
      return;
    }

    try {
      const indicators = getValues(indicatorsPath) ?? [];
      const config: IndicatorTemplateConfig = {
        type: 'indicators',
        value: formIndicatorsToTemplateValue(indicators)
      };

      if (isEditMode && !templateId) {
        setError('Missing template id.');
        return;
      }

      const result = await saveTemplate({
        templateId,
        name: trimmedName,
        isBundle: false,
        config,
        label: 'Indicator template'
      });

      onSaved?.({
        templateId: result.templateId,
        name: trimmedName,
        config: result.config
      });
      onClose();
    } catch (saveError) {
      const message =
        saveError instanceof Error
          ? saveError.message
          : 'Invalid indicators config.';
      setError(message);
    }
  };

  return {
    formApi,
    control,
    isEditMode,
    name,
    error,
    loading,
    setName,
    setError,
    handleSave,
    onClose
  };
};

export const IndicatorTemplateEditor: React.FC<
  IndicatorTemplateEditorWindowProps
> = ({ templateId, initialName, initialIndicators, onSaved }) => {
  const { closeWindow } = useWindow();
  const {
    formApi,
    isEditMode,
    name,
    control,
    setName,
    setError,
    onClose,
    handleSave,
    error,
    loading
  } = useIndicatorTemplateEditor({
    templateId,
    initialName,
    initialIndicators,
    onSaved,
    onClose: () => closeWindow('IndicatorTemplateEditor')
  });

  return (
    <FormProvider {...formApi}>
      <Box
        sx={{
          ...templateEditorSurfaceSx,
          display: 'flex',
          flexDirection: 'column',
          gap: 2
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 700 }}>
          {isEditMode ? 'Edit Indicator Template' : 'Create Indicator Template'}
        </Typography>

        <Typography variant="body2" color="text.secondary">
          Configure indicators for this template.
        </Typography>

        <TextField
          label="Template name"
          value={name}
          onChange={(event) => {
            setName(event.target.value);
            setError('');
          }}
          fullWidth
        />

        <IndicatorsArrayForm
          indicatorsPath={indicatorsPath}
          control={control}
          setValue={formApi.setValue}
        />

        {error ? (
          <Typography variant="caption" color="error" sx={{ display: 'block' }}>
            {error}
          </Typography>
        ) : null}

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
          <AppButton tone="secondary" onClick={onClose} disabled={loading}>
            Cancel
          </AppButton>
          <AppButton tone="primary" onClick={handleSave} disabled={loading}>
            Save
          </AppButton>
        </Box>
      </Box>
    </FormProvider>
  );
};
